import { create } from 'zustand';

import { useWorkspace } from '../state/workspace.js';

/**
 * "Go to source": open a project file in the Code tab with the caret at a
 * line / column. The request waits here until the Code editor shows that
 * file (components/Editor.tsx applies it and clears it).
 */

export interface RevealRequest {
  path: string;
  /** 1-based. */
  line: number;
  /** 1-based; 0 = start of the line. */
  column: number;
  /** Distinguishes repeated requests for the same spot. */
  seq: number;
}

export const useCodeReveal = create<{ pending: RevealRequest | null }>(() => ({ pending: null }));

let seq = 0;

/** Opens `path` in the Code tab at `line` (and `column`). */
export function openCodeAt(path: string, line = 1, column = 0): void {
  const clean = path.replace(/\\/g, '/').replace(/^\.?\//, '');
  useCodeReveal.setState({ pending: { path: clean, line: Math.max(1, Math.floor(line) || 1), column: Math.max(0, Math.floor(column) || 0), seq: ++seq } });
  const ws = useWorkspace.getState();
  void ws.openFile(clean);
  ws.setView('code');
}

/** Takes the pending request for `path` (null when there is none). */
export function takeReveal(path: string | null): RevealRequest | null {
  const pending = useCodeReveal.getState().pending;
  if (!pending || !path || pending.path !== path) return null;
  useCodeReveal.setState({ pending: null });
  return pending;
}
